import React, { useState, useRef, useEffect } from 'react';
import Markdown from 'react-markdown';
import { Camera, Upload, Loader2, AlertCircle, CheckCircle2, RefreshCw, ChevronLeft, History } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { User } from 'firebase/auth';
import { getUsageCount, incrementUsage, QUOTA_PLANT_DOCTOR } from '../utils/usageLimit';
import { savePlantDiagnosis, getPlantDiagnosisHistory } from '../lib/history';
import { PlantDiagnosis } from '../types';



interface PlantDoctorProps {
  user: User | null;
  language: 'en' | 'te' | 'hi';
  onBack: () => void;
}

const PlantDoctorScreen: React.FC<PlantDoctorProps> = ({ user, language, onBack }) => {
  const [image, setImage] = useState<string | null>(null);
  const [diagnosis, setDiagnosis] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [usageCount, setUsageCount] = useState<number>(0);
  const [history, setHistory] = useState<PlantDiagnosis[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      const count = await getUsageCount(user ? user.uid : null, 'plantDoctor');
      setUsageCount(count);
      
      if (user) {
        const h = await getPlantDiagnosisHistory(user.uid);
        setHistory(h);
      }
    };
    loadData();
  }, [user]);

  const t = {
    en: {
      title: 'Plant Doctor',
      subtitle: 'Upload a photo of your plant to diagnose pests and diseases.',
      uploadPhoto: 'Upload Photo',
      diagnose: 'Diagnose Plant',
      diagnosing: 'Diagnosing...',
      result: 'Diagnosis Result',
      reset: 'Start Over',
      error: 'Failed to diagnose. Please try again.',
      noImage: 'Please select an image first.',
      back: 'Back',
      limitReached: 'Daily limit reached. You can use this feature 2 times per day.',
      remainingUses: 'Remaining uses today: {count}',
      limitReachedAlert: 'You have used your 2 free Plant Doctor diagnoses for today. Please try again tomorrow!',
      quotaExhausted: 'Sorry, the server is busy because too many farmers are currently using it. Please try again after 10 minutes.'
    },
    te: {
      title: 'ప్లాంట్ డాక్టర్',
      subtitle: 'పంట తెగుళ్లు మరియు వ్యాధులను గుర్తించడానికి మీ మొక్క ఫోటోను అప్‌లోడ్ చేయండి.',
      uploadPhoto: 'ఫోటో అప్‌లోడ్ చేయండి',
      diagnose: 'మొక్కను పరీక్షించండి',
      diagnosing: 'పరీక్షిస్తున్నాము...',
      result: 'పరీక్షా ఫలితం',
      reset: 'మళ్ళీ ప్రారంభించండి',
      error: 'పరీక్షించడం విఫలమైంది. దయచేసి మళ్ళీ ప్రయత్నించండి.',
      noImage: 'దయచేసి ముందుగా ఒక చిత్రాన్ని ఎంచుకోండి.',
      back: 'వెనుకకు',
      limitReached: 'రోజువారీ పరిమితి ముగిసింది. మీరు ఈ సేవను రోజుకు 2 సార్లు మాత్రమే ఉపయోగించగలరు.',
      remainingUses: 'ఈరోజు మిగిలి ఉన్న ఉపయోగాల సంఖ్య: {count}',
      limitReachedAlert: 'మీరు ఈరోజుకు మీ 2 ఉచిత ప్లాంట్ డాక్టర్ నిర్ధారణలను ఉపయోగించారు. దయచేసి రేపు మళ్లీ ప్రయత్నించండి!',
      quotaExhausted: 'క్షమించండి, ప్రస్తుతం చాలా మంది రైతులు దీనిని ఉపయోగిస్తున్నందున సర్వర్ బిజీగా ఉంది. దయచేసి 10 నిమిషాల తర్వాత మళ్లీ ప్రయత్నించండి.'
    },
    hi: {
      title: 'प्लांट डॉक्टर',
      subtitle: 'कीटों और रोगों के निदान के लिए अपने पौधे की फोटो अपलोड करें।',
      uploadPhoto: 'फोटो अपलोड करें',
      diagnose: 'पौधे का निदान करें',
      diagnosing: 'निदान हो रहा है...',
      result: 'निदान परिणाम',
      reset: 'फिर से शुरू करें',
      error: 'निदान करने में विफल। कृपया पुनः प्रयास करें।',
      noImage: 'कृपया पहले एक छवि चुनें।',
      back: 'पीछे',
      limitReached: 'दैनिक सीमा समाप्त। आप इस सुविधा का उपयोग दिन में केवल 2 बार कर सकते हैं।',
      remainingUses: 'आज शेष उपयोग: {count}',
      limitReachedAlert: 'आपने आज के लिए अपने 2 मुफ्त प्लांट डॉक्टर निदान का उपयोग कर लिया है। कृपया कल पुनः प्रयास करें!',
      quotaExhausted: 'क्षमा करें, वर्तमान में बहुत अधिक किसान इसका उपयोग कर रहे हैं, इसलिए सर्वर व्यस्त है। कृपया 10 मिनट के बाद पुनः प्रयास करें।'
    }
  }[language];

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);
        setDiagnosis(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDiagnose = async () => {
    if (!image) {
      setError(t.noImage);
      return;
    }

    // Check usage limits
    const currentCount = await getUsageCount(user ? user.uid : null, 'plantDoctor');
    if (currentCount >= QUOTA_PLANT_DOCTOR) {
      setUsageCount(currentCount);
      setError(t.limitReachedAlert);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const mimeType = image.split(';')[0].split(':')[1] || "image/jpeg";
      const base64Data = image.split(',')[1];

      const res = await fetch("/api/gemini/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          model: "gemini-flash-latest",
          contents: [{
            parts: [
              { text: `You are a professional plant pathologist and natural farming expert. 
              Analyze this plant image and:
              1. Identify the plant.
              2. Identify any pests, diseases, or nutrient deficiencies.
              3. Provide a detailed diagnosis.
              4. Recommend ONLY Prakruthi Vyavasayam (Natural Farming) solutions (Kashayams, biological controls, soil health improvements).
              5. Strictly forbid chemical pesticides or fertilizers.
              
              Respond ONLY in ${language === 'te' ? 'Telugu' : language === 'hi' ? 'Hindi' : 'English'}. Do NOT use any other language.
              Use a helpful and encouraging tone for the farmer, using "${language === 'te' ? 'రైతు సోదరా' : language === 'hi' ? 'किसान भाई' : 'Farmer brother'}".` },
              { inlineData: { mimeType, data: base64Data } }
            ]
          }]
        })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Failed securely generating diagnosis.");
      }

      const response = await res.json();
      const result = response.text || t.error;
      setDiagnosis(result);

      if (user) {
        await savePlantDiagnosis(user.uid, image, result);
        const updatedHistory = await getPlantDiagnosisHistory(user.uid);
        setHistory(updatedHistory);
      }

      // Successfully diagnosed, increment and update state
      const newCount = await incrementUsage(user ? user.uid : null, 'plantDoctor');
      setUsageCount(newCount);
    } catch (err: any) {
      console.error("Diagnosis Error:", err);
      
      const errStr = String(err?.message || err || '').toLowerCase();
      const isQuotaExhausted = errStr.includes('resource_exhausted') || errStr.includes('429') || errStr.includes('quota');
      
      let errorMessage = isQuotaExhausted ? t.quotaExhausted : t.error;
      
      if (!isQuotaExhausted && err.message) {
        errorMessage += ` (${err.message})`;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#f8f9fa] overflow-y-auto pb-20">
      <div className="p-4 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-stone-100 rounded-full text-[#1b7d36]">
              <ChevronLeft size={24} />
            </button>
            <h2 className="text-xl font-bold text-[#1b7d36]">{t.title}</h2>
          </div>
          {user && (
            <button 
              onClick={() => setShowHistory(!showHistory)}
              className={`p-2 rounded-full transition-colors ${showHistory ? 'bg-[#1b7d36] text-white' : 'hover:bg-stone-100 text-[#1b7d36]'}`}
            >
              <History size={24} />
            </button>
          )}
        </div>

        {error && !diagnosis && (
          <div className="p-4 bg-rose-50 text-rose-600 rounded-2xl flex items-center gap-3 border border-rose-100">
            <AlertCircle size={20} />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        {showHistory ? (
          <div className="space-y-4">
            <h3 className="font-bold text-stone-700 flex items-center gap-2">
              <History size={18} /> {language === 'te' ? 'చరిత్ర' : language === 'hi' ? 'इतिहास' : 'Diagnosis History'}
            </h3>
            {history.length === 0 ? (
              <div className="p-10 text-center text-stone-400 text-sm">
                {language === 'te' ? 'చరిత్ర ఏమీ లేదు' : language === 'hi' ? 'कोई इतिहास नहीं' : 'No history found.'}
              </div>
            ) : (
              <div className="space-y-4">
                {history.map((item) => (
                  <div key={item.id} className="bg-white p-4 rounded-2xl border border-black/5 shadow-sm space-y-3">
                    <div className="flex gap-4">
                      <img 
                        src={item.image} 
                        alt="Diagnosed plant" 
                        className="w-20 h-20 rounded-xl object-cover"
                        referrerPolicy="no-referrer"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-stone-400 font-mono mb-1">
                          {new Date(item.timestamp as any).toLocaleString()}
                        </p>
                        <div className="text-xs text-stone-600 line-clamp-3">
                          <Markdown>{item.diagnosis}</Markdown>
                        </div>
                      </div>
                    </div>
                    <button 
                      onClick={() => {
                        setImage(item.image);
                        setDiagnosis(item.diagnosis);
                        setShowHistory(false);
                      }}
                      className="w-full py-2 bg-stone-50 text-[#1b7d36] text-xs font-bold rounded-lg hover:bg-stone-100 transition-colors"
                    >
                      {language === 'te' ? 'వివరాలు చూడండి' : language === 'hi' ? 'विवरण देखें' : 'View Full Diagnosis'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <p className="text-sm text-stone-600 leading-relaxed">
              {t.subtitle}
            </p>

        {/* Daily limit badge */}
        <div className="flex justify-between items-center p-4 bg-white rounded-2xl border border-black/5 text-xs font-medium text-stone-500 shadow-sm">
          <span className={usageCount >= QUOTA_PLANT_DOCTOR ? 'text-rose-500 font-semibold' : ''}>
            {usageCount >= QUOTA_PLANT_DOCTOR 
              ? t.limitReached 
              : t.remainingUses.replace('{count}', String(QUOTA_PLANT_DOCTOR - usageCount))}
          </span>
          <span className="text-[11px] text-stone-400 bg-stone-100 px-2.5 py-1 rounded-lg font-mono font-bold">
            {usageCount}/{QUOTA_PLANT_DOCTOR}
          </span>
        </div>

        {!image && (
          <div className="space-y-4">
            <div 
              onClick={() => galleryInputRef.current?.click()}
              className="aspect-square bg-white border-2 border-dashed border-stone-200 rounded-[40px] flex flex-col items-center justify-center gap-4 cursor-pointer hover:border-[#1b7d36] hover:bg-emerald-50 transition-all group"
            >
              <div className="w-16 h-16 rounded-full bg-stone-50 flex items-center justify-center text-stone-400 group-hover:bg-white group-hover:text-[#1b7d36] transition-colors">
                <Upload size={32} />
              </div>
              <span className="font-bold text-stone-500 group-hover:text-[#1b7d36]">{t.uploadPhoto}</span>
            </div>

            <input 
              type="file" 
              ref={galleryInputRef} 
              onChange={handleImageSelect} 
              accept="image/*" 
              className="hidden" 
            />
          </div>
        )}
        {image && (
          <div className="space-y-6">
            <div className="relative aspect-square rounded-[40px] overflow-hidden shadow-lg border-4 border-white">
              <img src={image} alt="Plant to diagnose" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              <button 
                onClick={() => { setImage(null); setDiagnosis(null); }}
                className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-md text-rose-500 hover:bg-rose-50 transition-colors"
              >
                <RefreshCw size={20} />
              </button>
            </div>

            {!diagnosis && !isLoading && (
              <button
                onClick={handleDiagnose}
                disabled={usageCount >= QUOTA_PLANT_DOCTOR}
                className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold shadow-lg hover:bg-[#144317] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#1b7d36] transition-all flex items-center justify-center gap-2"
              >
                <CheckCircle2 size={20} />
                {t.diagnose}
              </button>
            )}

            {isLoading && image && (
              <div className="w-full bg-emerald-50 text-[#1b7d36] py-8 rounded-2xl font-bold flex flex-col items-center justify-center gap-3 border border-emerald-100">
                <Loader2 className="animate-spin" size={32} />
                <span>{t.diagnosing}</span>
              </div>
            )}

            {error && diagnosis && (
              <div className="p-4 bg-rose-50 text-rose-600 rounded-2xl flex items-center gap-3 border border-rose-100">
                <AlertCircle size={20} />
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            {diagnosis && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-[32px] p-6 shadow-lg border border-black/5 space-y-4"
              >
                <div className="flex items-center gap-2 text-[#1b7d36] border-b border-stone-100 pb-3">
                  <CheckCircle2 size={20} />
                  <h3 className="font-bold">{t.result}</h3>
                </div>
                <div className="markdown-body text-sm leading-relaxed text-stone-700">
                  <Markdown>{diagnosis}</Markdown>
                </div>
                <button
                  onClick={() => { setImage(null); setDiagnosis(null); }}
                  className="w-full bg-stone-100 text-stone-600 py-3 rounded-xl font-bold hover:bg-stone-200 transition-colors mt-4"
                >
                  {t.reset}
                </button>
              </motion.div>
            )}
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlantDoctorScreen;
